import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { pharmacyApi } from "@/api/pharmacy";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";

type Drug = { id: string; name: string; strength?: string; form?: string; unit?: string };

const REASONS = [
  { id: "EMERGENCY_TRAY", label: "Emergency tray use" },
  { id: "EXPIRED", label: "Expired" },
  { id: "WASTAGE", label: "Wastage" },
  { id: "TRAINING", label: "Training" },
  { id: "OTHER", label: "Other" },
];

export default function OTGeneralConsumptionPage() {
  const qc = useQueryClient();
  const [reason, setReason] = useState("EMERGENCY_TRAY");
  const [notes, setNotes] = useState("");
  const [drugSearch, setDrugSearch] = useState("");
  const [items, setItems] = useState<Array<{ drugId: string; qty: string }>>([]);

  const { data: drugs = [] } = useQuery({
    queryKey: ["drugs-list"],
    queryFn: async () => {
      const res = await api.get<{ data?: Drug[] }>("/api/prescription/drugs");
      return res.data?.data ?? [];
    },
  });

  const filteredDrugs = useMemo(() => {
    const q = drugSearch.trim().toLowerCase();
    if (!q) return drugs;
    return drugs.filter((d) => d.name.toLowerCase().includes(q) || (d.strength ?? "").toLowerCase().includes(q));
  }, [drugs, drugSearch]);

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!reason) throw new Error("Select reason");
      if (items.length === 0) throw new Error("Add at least one drug");
      return pharmacyApi.createOTGeneralConsumption({
        reason,
        notes: notes.trim() || undefined,
        items: items.map((i) => ({ drugId: i.drugId, quantity: parseInt(i.qty, 10) })),
      });
    },
    onSuccess: async () => {
      toast.success("General consumption saved");
      setItems([]);
      setNotes("");
      await qc.invalidateQueries({ queryKey: ["ot-dashboard"] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || err?.message || "Failed"),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>OT General Consumption (Non-billed)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Reason *</label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REASONS.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Notes</label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Items</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input placeholder="Search drug to add..." value={drugSearch} onChange={(e) => setDrugSearch(e.target.value)} />
          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto border rounded-md p-2 bg-muted/30">
            {filteredDrugs.slice(0, 50).map((d) => (
              <Button key={d.id} size="sm" variant="outline" onClick={() => setItems((p) => [...p, { drugId: d.id, qty: "1" }])}>
                <Plus className="h-4 w-4 mr-2" />
                {d.name} {d.strength ? `(${d.strength})` : ""}
              </Button>
            ))}
          </div>

          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="text-left p-2">Drug</th>
                  <th className="text-right p-2">Qty</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={3} className="p-3 text-muted-foreground">No items added</td></tr>
                ) : (
                  items.map((it, idx) => {
                    const drug = drugs.find((d) => d.id === it.drugId);
                    return (
                      <tr key={`${it.drugId}-${idx}`} className="border-t">
                        <td className="p-2">{drug?.name} {drug?.strength ? `(${drug.strength})` : ""}</td>
                        <td className="p-2 text-right">
                          <Input type="number" min={1} step={1} value={it.qty} onChange={(e) => setItems((p) => p.map((x, i) => (i === idx ? { ...x, qty: e.target.value } : x)))} />
                        </td>
                        <td className="p-2 text-right">
                          <Button variant="outline" size="sm" onClick={() => setItems((p) => p.filter((_, i) => i !== idx))}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end">
            <Button onClick={() => createMutation.mutate()} disabled={createMutation.isPending}>
              {createMutation.isPending ? <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Saving...</span> : "Save"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

